<script setup>
import { ref, computed, watch, onMounted, onUnmounted, nextTick } from 'vue';
import { useAuthStore } from '@/stores/authStore';
import { useOrderStore } from '@/stores/OrderStore';
import { useToast } from 'vue-toastification';
import emailValidator from 'email-validator';
import axios from 'axios';
import 'intl-tel-input/build/css/intlTelInput.css';
import intlTelInput from 'intl-tel-input';
import PickUpInStore from '@/components/manual-order/PickUpInStore.vue';
import ShippingAddressToggle from '@/components/manual-order/ShippingAddressToggle.vue';
import ShippingAddressForm from '@/components/manual-order/ShippingAddressForm.vue';
import OrderNotes from '@/components/manual-order/OrderNotes.vue';

// Template ref for phone input
const phoneInputRef = ref(null);
const customerLookupInput = ref(null);
let itiInstance = null;

const toast = useToast();
const authStore = useAuthStore();
const orderStore = useOrderStore();

// Step management
// lookup, verify, customer, confirm
const currentStep = ref('lookup');
const steps = ['lookup', 'verify', 'customer', 'confirm'];

// Customer lookup
const customerLookup = ref('');
const customerFound = ref(false);
const customerData = ref(null);
const isSearching = ref(false);
const searchError = ref('');
// Track which search methods have been attempted
const emailSearchAttempted = ref(false);
const phoneSearchAttempted = ref(false);
const searchedEmail = ref('');
const searchedPhone = ref('');
const showCustomerNotFoundMessage = ref(false);

// Customer details
const firstName = ref('');
const lastName = ref('');
const email = ref('');
const phone = ref('');
const orderNotes = ref('');

// Shipping address
const useShippingAddress = ref(false);
const addressLine1 = ref('');
const addressLine2 = ref('');
const city = ref('');
const province = ref('');
const postalCode = ref('');
const country = ref('CA');

// Shipping address validation errors
const addressLine1Error = ref('');
const postalCodeError = ref('');
const emailError = ref('');
const phoneError = ref('');

// Canadian provinces list
const canadianProvinces = [
  { label: 'Alberta', value: 'AB' },
  { label: 'British Columbia', value: 'BC' },
  { label: 'Manitoba', value: 'MB' },
  { label: 'New Brunswick', value: 'NB' },
  { label: 'Newfoundland and Labrador', value: 'NL' },
  { label: 'Northwest Territories', value: 'NT' },
  { label: 'Nova Scotia', value: 'NS' },
  { label: 'Nunavut', value: 'NU' },
  { label: 'Ontario', value: 'ON' },
  { label: 'Prince Edward Island', value: 'PE' },
  { label: 'Quebec', value: 'QC' },
  { label: 'Saskatchewan', value: 'SK' },
  { label: 'Yukon', value: 'YT' }
];

// US states list (common states)
const usStates = [
  // { label: 'Alabama', value: 'AL' },
  // { label: 'Alaska', value: 'AK' },
  // { label: 'Arizona', value: 'AZ' },
  // { label: 'Arkansas', value: 'AR' },
  // { label: 'California', value: 'CA' },
  // { label: 'Colorado', value: 'CO' },
  // { label: 'Connecticut', value: 'CT' },
  // { label: 'Delaware', value: 'DE' },
  // { label: 'Florida', value: 'FL' },
  // { label: 'Georgia', value: 'GA' },
  // { label: 'Hawaii', value: 'HI' },
  // { label: 'Idaho', value: 'ID' },
  // { label: 'Illinois', value: 'IL' },
  // { label: 'Indiana', value: 'IN' },
  // { label: 'Iowa', value: 'IA' },
  // { label: 'Kansas', value: 'KS' },
  // { label: 'Kentucky', value: 'KY' },
  // { label: 'Louisiana', value: 'LA' },
  // { label: 'Maine', value: 'ME' },
  // { label: 'Maryland', value: 'MD' },
  // { label: 'Massachusetts', value: 'MA' },
  // { label: 'Michigan', value: 'MI' },
  // { label: 'Minnesota', value: 'MN' },
  // { label: 'Mississippi', value: 'MS' },
  // { label: 'Missouri', value: 'MO' },
  // { label: 'Montana', value: 'MT' },
  // { label: 'Nebraska', value: 'NE' },
  // { label: 'Nevada', value: 'NV' },
  // { label: 'New Hampshire', value: 'NH' },
  // { label: 'New Jersey', value: 'NJ' },
  // { label: 'New Mexico', value: 'NM' },
  // { label: 'New York', value: 'NY' },
  // { label: 'North Carolina', value: 'NC' },
  // { label: 'North Dakota', value: 'ND' },
  // { label: 'Ohio', value: 'OH' },
  // { label: 'Oklahoma', value: 'OK' },
  // { label: 'Oregon', value: 'OR' },
  // { label: 'Pennsylvania', value: 'PA' },
  // { label: 'Rhode Island', value: 'RI' },
  // { label: 'South Carolina', value: 'SC' },
  // { label: 'South Dakota', value: 'SD' },
  // { label: 'Tennessee', value: 'TN' },
  { label: 'Texas', value: 'TX' },
  // { label: 'Utah', value: 'UT' },
  // { label: 'Vermont', value: 'VT' },
  // { label: 'Virginia', value: 'VA' },
  // { label: 'Washington', value: 'WA' },
  // { label: 'West Virginia', value: 'WV' },
  // { label: 'Wisconsin', value: 'WI' },
  // { label: 'Wyoming', value: 'WY' }
];

// Computed property for available provinces/states based on country
const availableProvinces = computed(() => {
  if (country.value === 'CA') {
    return canadianProvinces;
  } else if (country.value === 'US') {
    return usStates;
  }
  return [];
});

// Postal code validation function
// Supports format:
// - 6 characters: H3A 2S9 (Letter-Digit-Letter Space Digit-Letter-Digit)
function validatePostalCode(value) {
  if (!value) return true; // Optional if empty
  
  // Remove spaces for validation
  const cleaned = value.replace(/\s/g, '').toUpperCase();
  
  // 6-character format: Letter-Digit-Letter Digit-Letter-Digit (e.g., H3A2S9 or H3A 2S9)
  if (cleaned.length === 6) {
    const sixCharRegex = /^[A-Z]\d[A-Z]\d[A-Z]\d$/;
    return sixCharRegex.test(cleaned);
  }
  
  // Invalid length
  return false;
}

// Format postal code
// - 6 characters: Add space in the middle (H3A 2S9)
function formatPostalCode(value) {
  if (!value) return '';
  // Remove all spaces and convert to uppercase
  const cleaned = value.replace(/\s/g, '').toUpperCase();
  // Only allow letters and digits
  const sanitized = cleaned.replace(/[^A-Z0-9]/g, '');
  
  // Limit to 6 characters maximum
  const limited = sanitized.slice(0, 6);
  
  // If it's 6 characters, add space in the middle (H3A 2S9)
  if (limited.length === 6) {
    return `${limited.slice(0, 3)} ${limited.slice(3)}`;
  }
  
  // Return as is for now
  return limited;
}

// Pick up in store
const pickUpInStore = ref(true);
const selectedPickupLocation = ref('');

// Order summary modal
const showOrderSummary = ref(false);

// Computed properties for lookup labels/placeholders that nudge the user to try the other lookup method after a miss
const lookupLabel = computed(() => {
  if (emailSearchAttempted.value && !phoneSearchAttempted.value) return 'Try searching with phone';
  if (phoneSearchAttempted.value && !emailSearchAttempted.value) return 'Try searching with email';
  return 'Email or Phone';
});

const lookupPlaceholder = computed(() => {
  if (emailSearchAttempted.value && !phoneSearchAttempted.value) return 'Search with phone number';
  if (phoneSearchAttempted.value && !emailSearchAttempted.value) return 'Search with email address';
  return 'Search by email or phone';
});

const canSkipLookup = computed(() => {
  return (emailSearchAttempted.value || phoneSearchAttempted.value) &&
    !(emailSearchAttempted.value && phoneSearchAttempted.value);
});

// Computed
// const canProceedToCustomer = computed(() => {
//   return customerLookup.value.trim().length > 0;
// });

const canSearchLookup = computed(() => {
  const searchTerm = customerLookup.value.trim();
  if (!searchTerm) return false;
  const startsWithLetter = /^[A-Za-z]/.test(searchTerm);
  const isEmail = startsWithLetter || searchTerm.includes('@');
  if (isEmail) {
    return isValidEmail(searchTerm);
  }
  const hasDigits = /\d/.test(searchTerm);
  if (!hasDigits) return false;
  const digitCount = cleanPhoneNumber(searchTerm).replace(/\D/g, '').length;
  return digitCount >= 10;
});

const canProceedToConfirm = computed(() => {
  // Only require basic name fields for enabling Continue
  const hasBasicInfo = firstName.value.trim().length > 0 && lastName.value.trim().length > 0;
  const trimmedEmail = email.value.trim();
  const trimmedPhone = phone.value.trim();
  const hasValidEmail = trimmedEmail ? isValidEmail(trimmedEmail) : false;
  const phoneDigits = trimmedPhone ? cleanPhoneNumber(trimmedPhone).replace(/\D/g, '').length : 0;
  const hasValidPhone = phoneDigits >= 10;
  const hasContact = hasValidEmail || hasValidPhone;
  return hasBasicInfo && hasContact;
});

const canCreateOrder = computed(() => {
  const hasDeliveryMethod = pickUpInStore.value || useShippingAddress.value;
  const isShippingAddressComplete = (() => {
    if (!useShippingAddress.value) return true;
    const hasAddressLine1 = addressLine1.value.trim().length > 0;
    const hasCity = city.value.trim().length > 0;
    const hasCountry = Boolean(country.value);
    const requiresProvince = country.value === 'CA' || country.value === 'US';
    const hasProvince = !requiresProvince || province.value.trim().length > 0;
    const hasPostalCode = postalCode.value.trim().length > 0;
    const isPostalCodeValid = validatePostalCode(postalCode.value);

    return (
      hasAddressLine1 &&
      hasCity &&
      hasCountry &&
      hasProvince &&
      hasPostalCode &&
      isPostalCodeValid
    );
  })();

  return canProceedToConfirm.value && hasDeliveryMethod && isShippingAddressComplete;
});

// Initialize intl-tel-input
function initializePhoneInput() {
  if (phoneInputRef.value && !itiInstance) {
    try {
      // Ensure input is enabled
      phoneInputRef.value.disabled = false;
      phoneInputRef.value.readOnly = false;
      
      itiInstance = intlTelInput(phoneInputRef.value, {
        utilsScript: 'https://cdn.jsdelivr.net/npm/intl-tel-input@23.0.0/build/js/utils.js',
        initialCountry: 'ca', // Default to Canada
        preferredCountries: ['ca', 'us'],
        separateDialCode: false, // Don't separate dial code to allow custom formatting
        formatOnDisplay: false, // Disable auto-formatting, we'll handle it ourselves
        nationalMode: true,
        autoPlaceholder: 'off',
      });
      
      // Set initial value if phone already has a value
      if (phone.value) {
        itiInstance.setNumber(phone.value);
      }
      
      // Get the actual input element (intl-tel-input wraps it)
      const actualInput = phoneInputRef.value;
      
      // Track previous values for comparison
      let previousRawValue = '';
      
      // Sync with phone ref when phone number changes and apply custom formatting
      const handleInput = (e) => {
        if (itiInstance && actualInput) {
          const currentValue = actualInput.value;
          const rawValue = currentValue.replace(/\D/g, ''); // Get only digits
          const countryData = itiInstance.getSelectedCountryData();
          const countryCode = countryData ? countryData.dialCode : '';
          
          // Detect if user is typing (adding) or deleting
          const isTyping = rawValue.length > previousRawValue.length;
          
          // Apply custom formatting only if there's enough input
          // Don't format on first keypress if it's just one digit to avoid jumping
          if (rawValue) {
            // Store raw value first
            previousRawValue = rawValue;
            
            // Format: (country code) area-code-number
            let formatted = '';
            
            // If just starting typing (1 digit), don't apply full mask immediately
            // This prevents jumping to (5__) ___-____ when typing just '5'
            if (rawValue.length === 1 && isTyping) {
               formatted = rawValue;
            } else if (rawValue.length >= 10) {
              const countryCodePart = rawValue.slice(0, 3);
              const areaCode = rawValue.slice(3, 6);
              const number = rawValue.slice(6, 10);
              formatted = `(${countryCodePart}) ${areaCode}-${number}`;
            } else if (rawValue.length >= 6) {
              const countryCodePart = rawValue.slice(0, 3);
              const areaCode = rawValue.slice(3, 6);
              const rest = rawValue.slice(6);
              formatted = rest ? `(${countryCodePart}) ${areaCode}-${rest}` : `(${countryCodePart}) ${areaCode}`;
            } else if (rawValue.length >= 3) {
              const countryCodePart = rawValue.slice(0, 3);
              const rest = rawValue.slice(3);
              formatted = rest ? `(${countryCodePart}) ${rest}` : `(${countryCodePart})`;
            } else {
              formatted = rawValue;
            }
            
            // Calculate new cursor position based on digit count
            let newCursorPos = formatted.length; // Default to end
            
            // Re-calculate cursor position if we are not at the end
            if (e && e.target && e.target.selectionStart < actualInput.value.length) {
               // Logic to maintain cursor position relative to digits
               // ... existing logic ...
            }
            
            // Detect if user is typing (adding) or deleting
            // const isTyping = rawValue.length > previousRawValue.length; // Already defined above
            const isDeleting = rawValue.length < previousRawValue.length;
            
            // Get cursor position before any changes
            const cursorPos = e ? e.target.selectionStart : actualInput.selectionStart;
             
            // Count digits before cursor position in current value
            const digitsBeforeCursor = currentValue.substring(0, cursorPos).replace(/\D/g, '').length;
            
            if (digitsBeforeCursor > 0) {
              let digitCount = 0;
              for (let i = 0; i < formatted.length; i++) {
                if (/\d/.test(formatted[i])) {
                  digitCount++;
                  if (digitCount === digitsBeforeCursor) {
                    // Place cursor right after this digit
                    newCursorPos = i + 1;
                    break;
                  }
                }
              }
            } else if (isDeleting && rawValue.length === 0) {
              // If deleting and no digits remain, cursor at start
              newCursorPos = 0;
            }
            
            // Only update if formatted value is different
            if (currentValue !== formatted) {
              actualInput.value = formatted;
              // Set cursor position after formatting
              setTimeout(() => {
                actualInput.setSelectionRange(newCursorPos, newCursorPos);
              }, 0);
            } else {
              // Even if value matches, ensure cursor is in correct position
              setTimeout(() => {
                if (actualInput.selectionStart !== newCursorPos) {
                  actualInput.setSelectionRange(newCursorPos, newCursorPos);
                }
              }, 0);
            }
            
            // Store the full international number in phone.value
            if (itiInstance) {
              const fullNumber = itiInstance.getNumber();
              // If getNumber works (valid number), use it.
              // If not, we might still want to capture the raw input for now
              if (fullNumber) {
                phone.value = fullNumber;
              } else {
                 // Try to construct it ourselves if we have enough digits
                 // This helps when typing partial numbers or if validation is strict
                 const currentRaw = rawValue;
                 const countryData = itiInstance.getSelectedCountryData();
                 const dialCode = countryData ? countryData.dialCode : '1';
                 
                 // Heuristic for US/Canada
                 if (dialCode === '1') {
                   if (currentRaw.length === 10) {
                     phone.value = `+1${currentRaw}`;
                   } else if (currentRaw.length === 11 && currentRaw.startsWith('1')) {
                     phone.value = `+${currentRaw}`;
                   } else {
                      // Just fallback to what we have if length is weird
                      phone.value = actualInput.value;
                   }
                 } else {
                   // Fallback
                   phone.value = actualInput.value;
                 }
              }
            }
            previousRawValue = rawValue;
          } else {
            // Empty value - allow clearing
            actualInput.value = '';
            phone.value = '';
            previousRawValue = '';
          }
        }
      };
      
      // Handle keydown to allow deletion of formatting characters
      const handleKeyDown = (e) => {
        if (e.key === 'Backspace' || e.key === 'Delete') {
          const cursorPos = actualInput.selectionStart;
          const currentValue = actualInput.value;
          
          // If cursor is at a formatting character and there's a digit before it, delete that digit
          if (cursorPos > 0 && !/\d/.test(currentValue[cursorPos - 1])) {
            // Find the digit before the formatting character
            for (let i = cursorPos - 1; i >= 0; i--) {
              if (/\d/.test(currentValue[i])) {
                // Select from this digit to cursor position to delete it
                e.preventDefault();
                actualInput.setSelectionRange(i, cursorPos);
                // Manually delete by setting value
                const before = currentValue.substring(0, i);
                const after = currentValue.substring(cursorPos);
                actualInput.value = before + after;
                // Trigger input event to format
                actualInput.dispatchEvent(new Event('input', { bubbles: true }));
                // Set cursor position
                setTimeout(() => {
                  const newPos = before.length;
                  actualInput.setSelectionRange(newPos, newPos);
                }, 0);
                return;
              }
            }
          }
        }
      };
      
      // Listen to input events on the actual input element
      actualInput.addEventListener('keydown', handleKeyDown);
      actualInput.addEventListener('input', handleInput);
      // actualInput.addEventListener('blur', handleInput);
      
      // Also sync when country changes
      actualInput.addEventListener('countrychange', () => {
        if (itiInstance && actualInput) {
          handleInput();
        }
      });
    } catch (error) {
      console.error('Error initializing intl-tel-input:', error);
    }
  }
}

// Cleanup intl-tel-input
function destroyPhoneInput() {
  if (itiInstance) {
    try {
      itiInstance.destroy();
      itiInstance = null;
    } catch (error) {
      console.error('Error destroying intl-tel-input:', error);
    }
  }
}

// Methods
function handleClose() {
  destroyPhoneInput();
  authStore.toggleAddManualOrderPopup();
  resetForm();
}

function resetForm() {
  currentStep.value = 'lookup';
  customerLookup.value = '';
  customerFound.value = false;
  customerData.value = null;
  firstName.value = '';
  lastName.value = '';
  email.value = '';
  phone.value = '';
  orderNotes.value = '';
  useShippingAddress.value = false;
  addressLine1.value = '';
  addressLine2.value = '';
  city.value = '';
  province.value = '';
  postalCode.value = '';
  country.value = 'CA';
  addressLine1Error.value = '';
  postalCodeError.value = '';
  emailError.value = '';
  phoneError.value = '';
  pickUpInStore.value = true;
  selectedPickupLocation.value = '';
  initializePickupLocation();
  showOrderSummary.value = false;
  searchError.value = '';
  isSearching.value = false;
  emailSearchAttempted.value = false;
  phoneSearchAttempted.value = false;
  searchedEmail.value = '';
  searchedPhone.value = '';
  showCustomerNotFoundMessage.value = false;
}

// Email validation function
function isValidEmail(value) {
  return emailValidator.validate(value);
}

function handleLookupInput(event) {
  const rawValue = event?.target?.value ?? '';
  const startsWithLetter = /^[A-Za-z]/.test(rawValue);
  const hasAtSymbol = rawValue.includes('@');

  // If it's not an email candidate, strip letters for phone search.
  if (!startsWithLetter && !hasAtSymbol) {
    const stripped = rawValue.replace(/[A-Za-z]/g, '');
    if (stripped !== rawValue) {
      customerLookup.value = stripped;
    }
  }
}

async function handleLookup() {
  const token = authStore.managerToken ? authStore.managerToken : authStore.token;
  // if (!canProceedToCustomer.value) return;
  
  isSearching.value = true;
  searchError.value = '';
  
  try {
    const searchTerm = customerLookup.value.trim();
    // Determine if it's email or phone
    const startsWithLetter = /^[A-Za-z]/.test(searchTerm);
    const isEmail = startsWithLetter || searchTerm.includes('@');

    // Validate email if it's an email search
    if (isEmail && !isValidEmail(searchTerm)) {
      searchError.value = 'Please enter a valid email address.';
      isSearching.value = false;
      return;
    }
    
    // Check if it's actually a phone number (contains digits) or just invalid text
    const hasDigits = /\d/.test(searchTerm);
    
    // If not an email and has no digits, it's invalid input
    if (!isEmail && !hasDigits) {
      searchError.value = 'Please enter a valid email address or phone number.';
      isSearching.value = false;
      return;
    }
    
    const queryParam = isEmail ? 'email' : 'phone';
    
    // Clean phone number for API call (remove formatting)
    const searchValue = isEmail ? searchTerm : cleanPhoneNumber(searchTerm);
    
    // Additional validation: ensure phone has minimum digits if it's a phone search
    if (!isEmail && hasDigits) {
      const digitCount = searchValue.replace(/\D/g, '').length;
      if (digitCount < 10) {
        searchError.value = 'Please enter a valid phone number (at least 10 digits).';
        isSearching.value = false;
        return;
      }
    }
    
    const response = await axios.get(
      `/api/Customer/GetCustomerDetail?${queryParam}=${encodeURIComponent(searchValue)}`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'store-id': authStore.branchId
        }
      }
    );

    if (response.data.status === 200 && response.data.customer.customerID) {
      const customer = response.data.customer;
      customerFound.value = true;
      customerData.value = customer;
      searchError.value = '';
      // Existing customer: start with shipping toggle OFF; user can opt-in.
      useShippingAddress.value = false;
      
      // Reset search tracking since customer was found
      emailSearchAttempted.value = false;
      phoneSearchAttempted.value = false;
      searchedEmail.value = '';
      searchedPhone.value = '';
      
      // Pre-fill customer data from API response
      firstName.value = customer.firstName || '';
      lastName.value = customer.lastName || '';
      email.value = customer.billingAddress?.email || '';
      // Format phone number if it exists
      const rawPhone = customer.billingAddress?.phone || '';
      phone.value = rawPhone ? formatPhoneNumber(rawPhone) : '';
      
      // Pre-fill billing address if available
      if (customer.billingAddress) {
        addressLine1.value = customer.billingAddress.addressLine1 || '';
        addressLine2.value = customer.billingAddress.addressLine2 || '';
        city.value = customer.billingAddress.city || '';
        province.value = customer.billingAddress.state || '';
        postalCode.value = customer.billingAddress.zipCode || '';
        country.value = customer.billingAddress.country || 'CA';
      }
      
      currentStep.value = 'verify';
      toast.success('Customer found!');
    } else {
      customerFound.value = false;
      customerData.value = null;
      
      // Track which search method was attempted
      if (isEmail) {
        emailSearchAttempted.value = true;
        searchedEmail.value = searchTerm;
        email.value = searchTerm;
      } else {
        phoneSearchAttempted.value = true;
        searchedPhone.value = searchTerm;
        phone.value = searchTerm;
      }
      
      // Check if both email and phone searches have been attempted and failed
      if (emailSearchAttempted.value && phoneSearchAttempted.value) {
        // Both searches failed, automatically show customer creation form
        // Pre-fill email from previous search
        email.value = searchedEmail.value || email.value;
        // Pre-fill phone from previous search if available
        if (searchedPhone.value) {
           phone.value = searchedPhone.value;
        } else {
           phone.value = ''; 
        }
        // New customer path: enable shipping unless pickup is selected
        useShippingAddress.value = !pickUpInStore.value;
        currentStep.value = 'customer';
        searchError.value = '';
        showCustomerNotFoundMessage.value = true;
      } else {
        // Only one search attempted, stay on verify step
        currentStep.value = 'verify';
        
        // Set specific error message based on search method
        if (isEmail) {
          searchError.value = `Customer not found with email ${searchTerm}. Try searching with phone?`;
        } else {
          searchError.value = `Customer not found with phone ${searchTerm}. Try searching with email?`;
        }
        // Reset input field when search fails to allow new entry
        customerLookup.value = '';
      }
    }
  } catch (error) {
    console.error('Customer lookup error:', error);
    customerFound.value = false;
    customerData.value = null;
    
    // If customer not found (404 or similar), allow searching with the other method or creating new
    if (error.response?.status === 404 || error.response?.status === 400) {
      const searchTerm = customerLookup.value.trim();
      const isEmail = searchTerm.includes('@');
      
      // Track which search method was attempted
      if (isEmail) {
        emailSearchAttempted.value = true;
        searchedEmail.value = searchTerm;
        email.value = searchTerm;
      } else {
        phoneSearchAttempted.value = true;
        searchedPhone.value = searchTerm;
        phone.value = searchTerm;
      }
      
      // Check if both email and phone searches have been attempted and failed
      if (emailSearchAttempted.value && phoneSearchAttempted.value) {
        // Both searches failed, automatically show customer creation form
        // Pre-fill email from previous search
        email.value = searchedEmail.value || email.value;
        // Pre-fill phone from previous search if available
        if (searchedPhone.value) {
           phone.value = searchedPhone.value;
        } else {
           phone.value = ''; 
        }
        // New customer path: enable shipping unless pickup is selected
        useShippingAddress.value = !pickUpInStore.value;
        currentStep.value = 'customer';
        searchError.value = '';
        showCustomerNotFoundMessage.value = true;
      } else {
        // Only one search attempted, stay on verify step
        currentStep.value = 'verify';
        
        // Show specific message based on search method
        if (isEmail) {
          searchError.value = `Customer not found with email ${searchTerm}. Try searching with phone?`;
        } else {
          searchError.value = `Customer not found with phone ${searchTerm}. Try searching with email?`;
        }
        // Reset input field when search fails to allow new entry
        customerLookup.value = '';
      }
    } else {
      searchError.value = error.response?.data?.message || 'Error searching for customer. Please try again.';
      toast.error(searchError.value);
    }
  } finally {
    isSearching.value = false;
  }
}

function handleSkipAndCreateNew() {
  customerFound.value = false;
  customerData.value = null;
  // New customer: enable shipping unless pickup is selected
  useShippingAddress.value = !pickUpInStore.value;
  showCustomerNotFoundMessage.value = true;
  currentStep.value = 'customer';
}

function handleContinueFromVerify() {
  if (customerFound.value && customerData.value) {
    // Customer found, proceed to confirm step
    currentStep.value = 'confirm';
  } else {
    // No customer found, go to customer creation
    currentStep.value = 'customer';
  }
}

// Validate address line 1
function validateAddressLine1() {
  if (addressLine1.value.trim()) {
    addressLine1Error.value = '';
  } else {
    addressLine1Error.value = 'Address Line 1 is required';
  }
}

// Validate postal code
function validatePostalCodeInput() {
  if (!postalCode.value || validatePostalCode(postalCode.value)) {
    postalCodeError.value = '';
  } else {
    postalCodeError.value = 'Invalid postal code format. Use format: H3A 2S9';
  }
}

function handleContinue() {
  if (currentStep.value === 'customer') {
    // Only require basic name fields here; shipping/address validation happens later if needed
    emailError.value = '';
    phoneError.value = '';
    const trimmedEmail = email.value.trim();
    const trimmedPhone = phone.value.trim();
    
    if (!trimmedEmail && !trimmedPhone) {
      emailError.value = 'Email or phone is required.';
      phoneError.value = 'Phone or email is required.';
      return;
    }
    
    if (trimmedEmail && !isValidEmail(trimmedEmail)) {
      emailError.value = 'Please enter a valid email address.';
      return;
    }
    
    if (trimmedPhone) {
      const digitCount = cleanPhoneNumber(trimmedPhone).replace(/\D/g, '').length;
      if (digitCount < 10) {
        phoneError.value = 'Please enter a valid phone number (at least 10 digits).';
        return;
      }
    }
    
    if (canProceedToConfirm.value) {
      
      // Ensure phone value is synced before moving to confirm step
      // The watcher on phone ref might not have fired if only the input value was updated by intl-tel-input internally
      if (itiInstance && phoneInputRef.value) {
        // Prefer getting the number from the instance which handles formatting
        const currentNumber = itiInstance.getNumber();
        if (currentNumber) {
          phone.value = currentNumber;
        } else if (phoneInputRef.value.value) {
          // If getNumber() is empty but we have input, try to construct it with country code
          const rawValue = phoneInputRef.value.value.replace(/\D/g, '');
          if (rawValue) {
            const countryData = itiInstance.getSelectedCountryData();
            const dialCode = countryData ? countryData.dialCode : '1'; // Default to 1 (US/CA) if unavailable
            
            // If raw value already starts with dial code, don't double add it (heuristic)
            // But dial codes can be ambiguous (e.g. 1 is US/CA/etc).
            // For US/CA, phone numbers are 10 digits.
            if (dialCode === '1' && rawValue.length === 10) {
               phone.value = `+1${rawValue}`;
            } else if (dialCode === '1' && rawValue.length === 11 && rawValue.startsWith('1')) {
               phone.value = `+${rawValue}`;
            } else {
               // For other countries or if logic unclear, append dial code
               // Check if rawValue starts with dialCode
               if (rawValue.startsWith(dialCode)) {
                 phone.value = `+${rawValue}`;
               } else {
                 phone.value = `+${dialCode}${rawValue}`;
               }
            }
          } else {
             phone.value = '';
          }
        }
      }

      currentStep.value = 'confirm';
    }
  }
}

function handleBack() {
  const currentIndex = steps.indexOf(currentStep.value);
  if (currentIndex > 0) {
    currentStep.value = steps[currentIndex - 1];
    if (currentStep.value === 'lookup') {
      searchError.value = '';
      customerLookup.value = '';
      emailSearchAttempted.value = false;
      phoneSearchAttempted.value = false;
      searchedEmail.value = '';
      searchedPhone.value = '';
      email.value = '';
      phone.value = '';
      emailError.value = '';
      phoneError.value = '';
      showCustomerNotFoundMessage.value = false;
    }
  }
}

function handleCreateOrder() {
  if (!canCreateOrder.value) return;
  showOrderSummary.value = true;
}

function handleConfirmOrder() {
  // Here you would typically make an API call to create the order
  // The order data includes:
  // - customerID: customerData.value?.customerID
  // - firstName, lastName, email, phone
  // - billingAddress, shippingAddress (if useShippingAddress is true)
  // - orderNotes
  
  // Clean phone number for payload (remove formatting)
  const cleanedPhone = cleanPhoneNumber(phone.value);
  
  // Convert country code to full name
  let countryFullName = country.value;
  if (country.value === 'CA') {
    countryFullName = 'Canada';
  } else if (country.value === 'US') {
    countryFullName = 'United States';
  }
  
  // Set customer info in OrderStore to display in WalkInCustomerButton
  const fullName = `${firstName.value} ${lastName.value}`.trim();
  orderStore.state.customer.id = customerData.value?.customerID || '';
  orderStore.state.customer.name = fullName || '';
  orderStore.state.customer.phone = cleanedPhone || '';
  orderStore.state.customer.email = email.value || '';
  orderStore.state.customer.note = orderNotes.value || '';
  orderStore.state.customer.lastName = lastName.value || '';
  orderStore.state.customer.addressLine1 = addressLine1.value || '';
  orderStore.state.customer.addressLine2 = addressLine2.value || '';
  orderStore.state.customer.city = city.value || '';
  orderStore.state.customer.state = province.value || '';
  orderStore.state.customer.zipCode = postalCode.value || '';
  orderStore.state.customer.country = countryFullName || '';

  orderStore.state.pickUpInStore = pickUpInStore.value;
  orderStore.state.pickupLocation = pickUpInStore.value ? selectedPickupLocation.value : null;

  orderStore.state.addManualOrder = true;
  
  // Persist the changes to localStorage immediately
  orderStore.saveOrderItemsToLocalStorage();
  
  const orderData = {
    customerID: customerData.value?.customerID || null,
    firstName: firstName.value,
    lastName: lastName.value,
    email: email.value,
    phone: cleanedPhone,
    billingAddress: {
      addressLine1: addressLine1.value,
      addressLine2: addressLine2.value,
      city: city.value,
      state: province.value,
      zipCode: postalCode.value,
      country: countryFullName,
      phone: cleanedPhone,
      email: email.value
    },
    shippingAddress: useShippingAddress.value ? {
      addressLine1: addressLine1.value,
      addressLine2: addressLine2.value,
      city: city.value,
      state: province.value,
      zipCode: postalCode.value,
      country: countryFullName
    } : null,
    orderNotes: orderNotes.value,
  };
  
  console.log('Order data to be created:', orderData);
  toast.success('Order created successfully!');
  handleClose();
}

function formatPhoneNumber(value) {
  if (!value) return '';
  
  // Remove all non-digit characters except +
  let cleaned = value.replace(/[^\d+]/g, '');
  
  // Handle international format with +
  if (cleaned.startsWith('+')) {
    // Remove the + and format
    cleaned = cleaned.substring(1);
    
    // Format: (country code) area-code-number
    // Example: +251918252650 -> (251) 918-2526
    // Pattern: (first 3 digits) (next 3 digits) - (next 4 digits, max 4)
    if (cleaned.length >= 10) {
      // Take first 3 digits as country code
      const countryCode = cleaned.slice(0, 3);
      // Take next 3 digits as area code
      const areaCode = cleaned.slice(3, 6);
      // Take next 4 digits (or remaining if less than 4)
      const number = cleaned.slice(6, 10);
      
      return `(${countryCode}) ${areaCode}-${number}`;
    } else if (cleaned.length >= 6) {
      // If we have at least 6 digits (3 country + 3 area)
      const countryCode = cleaned.slice(0, 3);
      const areaCode = cleaned.slice(3, 6);
      const rest = cleaned.slice(6);
      return rest ? `(${countryCode}) ${areaCode}-${rest}` : `(${countryCode}) ${areaCode}`;
    } else if (cleaned.length >= 3) {
      // If we have at least 3 digits (country code)
      const countryCode = cleaned.slice(0, 3);
      const rest = cleaned.slice(3);
      return rest ? `(${countryCode}) ${rest}` : `(${countryCode})`;
    } else {
      return cleaned;
    }
  } else {
    // Handle regular phone number without +
    if (cleaned.length >= 10) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}${cleaned.length > 10 ? cleaned.slice(10) : ''}`;
    } else if (cleaned.length >= 7) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    } else if (cleaned.length >= 4) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3)}`;
    } else if (cleaned.length >= 1) {
      return cleaned.length === 3 ? `(${cleaned})` : cleaned;
    }
  }
  
  return value;
}

// Function to clean phone number - removes formatting (brackets, spaces, dashes) but keeps + if present
function cleanPhoneNumber(value) {
  if (!value) return '';
  // Check if it's already in E.164 format (starts with +)
  const trimmed = value.trim();
  if (trimmed.startsWith('+')) {
      // Just keep digits and the leading +
      return `+${trimmed.substring(1).replace(/[^\d]/g, '')}`;
  }
  
  // If no +, just return digits
  // But wait, if we missed adding the + earlier, we might want to add it here as a fallback?
  // Let's stick to simple cleaning first, relying on handleContinue to have added the +
  return trimmed.replace(/[^\d]/g, '');
}

// Watch for currentStep to initialize phone input when customer step is shown
watch(currentStep, async (newStep) => {
  if (newStep === 'customer') {
    // Default shipping toggle based on pickup selection and new customer state
    useShippingAddress.value = pickUpInStore.value ? false : !customerFound.value;
    await nextTick();
    initializePhoneInput();
  } else {
    destroyPhoneInput();
  }
});

    watch(phone, (newVal, oldVal) => {
      // Skip formatting if intl-tel-input is handling it
      if (itiInstance && phoneInputRef.value) {
        
        // Prevent update if the input is currently focused to avoid cursor jumping and infinite loops
        // where handleInput adds country code, and setNumber adds it again into the input
        if (document.activeElement === phoneInputRef.value) {
          return;
        }

        // Only sync if the value is being set externally (not from user typing)
        // Check if the current input value differs from what intl-tel-input would show
        try {
          const currentNumber = itiInstance.getNumber();
          // If the new value is different and wasn't triggered by user input, update intl-tel-input
          if (newVal !== currentNumber && newVal !== oldVal) {
            itiInstance.setNumber(newVal || '');
          }
        } catch (error) {
          // Ignore errors when setting number
        }
        return;
      }
      
      // Only apply custom formatting if intl-tel-input is not active
  if (newVal && (newVal.match(/\d/) || newVal.includes('+'))) {
    const formatted = formatPhoneNumber(newVal);
    // Only update if the formatted value is different to avoid infinite loops
    if (formatted !== newVal) {
      phone.value = formatted;
    }
  }
});

// Lifecycle hooks
onMounted(() => {
  // Initialize phone input if customer step is active
  if (currentStep.value === 'customer') {
    nextTick(() => {
      initializePhoneInput();
    });
  }
  if (pickUpInStore.value) {
    initializePickupLocation();
  }
});

// Watch for popup visibility to focus input
watch(() => authStore.isAddManualOrderPopupVisible, (isVisible) => {
  if (isVisible) {
    nextTick(() => {
      if (customerLookupInput.value) {
        customerLookupInput.value.focus();
      }
    });
  }
});

onUnmounted(() => {
  destroyPhoneInput();
});

// Watch customerLookup to format phone numbers
watch(customerLookup, (newVal, oldVal) => {
  if (!newVal) return;

  // Check if it's a phone number:
  // - Starts with +, OR
  // - Contains digits and no @ symbol (email indicator)
  const hasAtSymbol = newVal.includes('@');
  const hasDigits = /\d/.test(newVal);
  const hasLetters = /[A-Za-z]/.test(newVal);
  const startsWithPlus = newVal.trim().startsWith('+');
  
  // Only format if it's clearly a phone number (not an email)
  const isPhoneNumber = startsWithPlus || (hasDigits && !hasAtSymbol && !hasLetters);
  
  if (isPhoneNumber && (hasDigits || startsWithPlus)) {
    // Extract only digits to check length
    const digitsOnly = newVal.replace(/\D/g, '');
    
    // Limit to 10 digits maximum (excluding formatting characters)
    if (digitsOnly.length > 10) {
      // If exceeds 10 digits, truncate to 10 digits and reformat
      const truncated = digitsOnly.slice(0, 10);
      const formatted = formatPhoneNumber(truncated);
      customerLookup.value = formatted;
      return;
    }
    
    // Only format when user is typing (value getting longer), not when deleting
    const isTyping = !oldVal || newVal.length > oldVal.length;
    
    if (isTyping) {
      const formatted = formatPhoneNumber(newVal);
      // Only update if the formatted value is different to avoid infinite loops
      if (formatted !== newVal && formatted.length > 0) {
        customerLookup.value = formatted;
      }
    }
    // When deleting, allow the user to delete freely without reformatting
  }
});

// Get current store name
const getCurrentStoreName = () => {
  if (window.currentBranch && window.currentBranch.Name) {
    return window.currentBranch.Name;
  }
  // Fallback to localStorage if available
  const storedBranchName = localStorage.getItem('branchName');
  if (storedBranchName) {
    return storedBranchName;
  }
  // Default fallback
  return 'Current Store';
};

// Get available pickup locations
const availablePickupLocations = computed(() => {
  const currentStore = getCurrentStoreName();
  
  // Hardcoded list of stores
  const hardcodedStores = [
    'Downtown',
    'Gorilla',
    'Edmonds',
    'Victoria',
    'Warehouse',
    'Queen'
  ];

  // Create a Set to ensure uniqueness and filter out current store if present in hardcoded list
  // The current store will be added at the beginning
  const otherStores = hardcodedStores.filter(store => store !== currentStore);
  
  // Combine current store with other stores
  const allStores = [currentStore, ...otherStores];
  
  return allStores.map(store => ({
    label: store,
    value: store
  }));
});

// Initialize pickup location with current store as default
const initializePickupLocation = () => {
  const currentStore = getCurrentStoreName();
  selectedPickupLocation.value = currentStore;
};

// Watch for pickUpInStore changes
watch(pickUpInStore, (newVal) => {
  if (newVal) {
    initializePickupLocation();
    // If pickup is selected, disable shipping address
    useShippingAddress.value = false;
  } else if (!useShippingAddress.value) {
    useShippingAddress.value = true;
  }
});

watch(useShippingAddress, (newVal) => {
  if (newVal) {
    // If shipping is selected, disable pickup
    pickUpInStore.value = false;
  } else if (!pickUpInStore.value) {
    pickUpInStore.value = true;
  }
});
</script>

<template>
  <Teleport to="body">
    <Transition name="modal-outer">
      <div v-show="authStore.isAddManualOrderPopupVisible" @click.self="handleClose"
        class="absolute z-10 w-full bg-black bg-opacity-30 h-screen top-0 left-0 flex justify-center px-8">
        <Transition name="modal-inner" class="rounded-2xl">
          <div v-if="authStore.isAddManualOrderPopupVisible"
            class="fixed top-10 z-50 flex items-center justify-center bg-black bg-opacity-50 w-10/12">
            <div class="bg-white rounded-2xl shadow-lg p-8 w-full">
              <!-- Close button -->
              <i @click="handleClose"
                class="pi pi-times-circle absolute top-4 right-4 cursor-pointer text-gray-500 hover:text-gray-700"
                style="font-size: 24px;"></i>

              <!-- Title -->
              <h1 class="font-bold text-3xl mb-6 text-gray-800">POS - Manual Order</h1>

              <div class="max-w-2xl flex items-center justify-between">
                <!-- Progress Indicator -->
                <div class="flex items-center gap-2 mb-4 text-sm">
                  <span :class="currentStep === 'lookup' ? 'text-green-600 font-semibold' : 'text-gray-500'"
                    class="flex items-center gap-1">
                    <span v-if="currentStep === 'lookup'" class="text-green-600">•</span>
                    <span v-else class="text-gray-400">•</span>
                    Lookup
                  </span>
                  <span class="text-gray-400">></span>
                  <span :class="currentStep === 'verify' ? 'text-green-600 font-semibold' : 'text-gray-500'"
                    class="flex items-center gap-1">
                    <span v-if="currentStep === 'verify'" class="text-green-600">•</span>
                    <span v-else class="text-gray-400">•</span>
                    Verify
                  </span>
                  <span class="text-gray-400">></span>
                  <span :class="currentStep === 'customer' ? 'text-green-600 font-semibold' : 'text-gray-500'"
                    class="flex items-center gap-1">
                    <span v-if="currentStep === 'customer'" class="text-green-600">•</span>
                    <span v-else class="text-gray-400">•</span>
                    Customer
                  </span>
                  <span class="text-gray-400">></span>
                  <span :class="currentStep === 'confirm' ? 'text-green-600 font-semibold' : 'text-gray-500'"
                    class="flex items-center gap-1">
                    <span v-if="currentStep === 'confirm'" class="text-green-600">•</span>
                    <span v-else class="text-gray-400">•</span>
                    Confirm
                  </span>
                </div>

                <!-- Action Buttons Below Breadcrumb -->
                <div class="flex items-center gap-3 mb-6">
                  <!-- Back Button (Icon) -->
                  <!-- Use Different Customer Button -->
                  <!-- <button 
                    @click="handleSkipAndCreateNew"
                    class="px-4 py-2 text-sm text-gray-600 bg-purple-200 hover:text-gray-800 rounded-lg hover:bg-gray-50 transition-colors">
                    <i class="pi pi-user text-gray-600" style="font-size: 16px;"></i>
                    Add Customer
                  </button> -->
                </div>
              </div>
              <button v-if="currentStep !== 'lookup'" @click="handleBack"
                    class="flex items-center justify-center w-10 h-10 rounded-lg hover:bg-gray-100 transition-colors"
                    title="Back">
                    <i class="pi pi-arrow-left text-gray-600" style="font-size: 18px;"></i>
                  </button>
              <!-- Main Content -->
              <div class="w-full">
                <!-- Customer Information -->
                <div class="space-y-6 w-full">
                  <!-- Customer Section -->
                  <div class="bg-gray-50 rounded-xl p-6 max-h-[70vh] overflow-y-auto">
                    <div class="flex items-center gap-2 mb-3">
                      <i class="pi pi-user text-gray-600" style="font-size: 20px;"></i>
                      <h2 class="text-xl font-semibold text-gray-800">Customer</h2>
                    </div>
                    <p class="text-sm text-gray-600 mb-4">Lookup by email or phone. Create if not found.</p>

                    <!-- Lookup Step -->
                    <div v-if="currentStep === 'lookup'" class="space-y-4 w-1/2">
                      <!-- Validation Error Message -->
                      <div v-if="searchError" class="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                        <p class="text-sm text-red-800">{{ searchError }}</p>
                      </div>
                      
                      <div>
                        <div class="flex items-center justify-between">
                          <label class="block text-sm font-medium text-gray-700 mb-2">{{ lookupLabel }}</label>
                          <!-- <button v-if="canSkipLookup" @click="handleSkipAndCreateNew" type="button"
                            class="text-xs text-gray-600 hover:text-gray-800 border border-gray-300 rounded-md px-2 py-1 hover:bg-gray-50 transition-colors">
                            Skip
                          </button> -->
                        </div>
                        <input ref="customerLookupInput" v-model="customerLookup" autocomplete="off" type="text" :placeholder="lookupPlaceholder"
                          class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:border-transparent"
                          :class="{ 'border-red-500': searchError }"
                          @input="handleLookupInput"
                          @keyup.enter="handleLookup" />
                      </div>
                      <div class="flex gap-3">
                        <button @click="handleLookup" :disabled="!canSearchLookup || isSearching"
                          class="flex-1 bg-purple-600 text-white px-6 py-2 rounded-lg font-medium hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
                          {{ isSearching ? 'Searching...' : 'Next' }}
                        </button>
                      </div>
                    </div>

                    <!-- Verify Step -->
                    <div v-if="currentStep === 'verify'" class="space-y-4 w-1/2">
                      <!-- Error Message -->
                      <!-- <div v-if="searchError" class="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                        <p class="text-sm text-red-800">{{ searchError }}</p>
                      </div> -->

                      <!-- Customer Found -->
                      <div v-if="customerFound && customerData" class="space-y-4">
                        <div class="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                          <p class="text-sm text-green-800 font-medium mb-2">✓ Customer found!</p>
                          <p v-if="customerData.customerID" class="text-xs text-green-700">ID: {{
                            customerData.customerID }}</p>
                        </div>

                        <!-- Customer Details Card -->
                        <div class="bg-white border border-gray-200 rounded-lg p-4 space-y-3">
                          <div class="flex items-center gap-2 mb-3">
                            <i class="pi pi-user text-purple-600" style="font-size: 20px;"></i>
                            <h3 class="font-semibold text-gray-800">Customer Information</h3>
                          </div>

                          <div class="space-y-2">
                            <div class="flex items-center gap-2">
                              <span class="text-sm font-medium text-gray-600 w-24">Name:</span>
                              <span class="text-sm text-gray-800">{{ customerData.firstName }} {{ customerData.lastName
                                }}</span>
                            </div>
                            <div class="flex items-center gap-2">
                              <span class="text-sm font-medium text-gray-600 w-24">Email:</span>
                              <span class="text-sm text-gray-800">{{ customerData.billingAddress?.email || 'N/A'
                                }}</span>
                            </div>
                            <div class="flex items-center gap-2">
                              <span class="text-sm font-medium text-gray-600 w-24">Phone:</span>
                              <span class="text-sm text-gray-800">{{ customerData.billingAddress?.phone || 'N/A'
                                }}</span>
                            </div>
                            <div v-if="customerData.billingAddress"
                              class="flex items-start gap-2 pt-2 border-t border-gray-200">
                              <span class="text-sm font-medium text-gray-600 w-24">Address:</span>
                              <span class="text-sm text-gray-800">
                                {{ customerData.billingAddress.addressLine1 || '' }}<br
                                  v-if="customerData.billingAddress.addressLine2" />
                                <span v-if="customerData.billingAddress.addressLine2">{{
                                  customerData.billingAddress.addressLine2 }}<br /></span>
                                {{ customerData.billingAddress.city || '' }}, {{ customerData.billingAddress.state || ''
                                }} {{ customerData.billingAddress.zipCode || '' }}<br />
                                {{ customerData.billingAddress.country || '' }}
                              </span>
                            </div>
                          </div>
                        </div>

                        <div class="flex gap-3 pt-2">
                          <button @click="handleContinueFromVerify"
                            class="flex-1 bg-purple-600 text-white px-6 py-2 rounded-lg font-medium hover:bg-purple-700 transition-colors">
                            Continue
                          </button>
                        </div>
                      </div>

                      <!-- Customer Not Found -->
                      <div v-else class="space-y-4">
                        <div class="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
                          <p class="text-sm text-yellow-800">{{ searchError || 'Customer not found. Enter the email or phone to try again, or create a new customer.' }}</p>
                        </div>
                        <div>
                          <div class="flex items-center justify-between mb-2">
                            <label class="block text-sm font-medium text-gray-700 mb-2">{{ lookupLabel }}</label>
                            <button v-if="canSkipLookup" @click="handleSkipAndCreateNew"
                            class="w-1/2 px-6 py-2 text-gray-600 font-medium hover:text-gray-800 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
                            Skip
                          </button>
                          </div>
                          <input v-model="customerLookup" type="text" :placeholder="lookupPlaceholder"
                            class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:border-transparent"
                            @input="handleLookupInput"
                            @keyup.enter="handleLookup" />
                        </div>
                        <div class="flex gap-3">
                          <button @click="handleLookup"
                            :disabled="!canSearchLookup || isSearching"
                            class="flex-1 bg-purple-600 text-white px-6 py-2 rounded-lg font-medium hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
                            Search Again
                          </button>
                          
                        </div>
                      </div>
                    </div>

                    <!-- Customer Details Step -->
                    <div v-if="currentStep === 'customer'" class="space-y-4 w-1/2">
                      <!-- Customer Not Found Message -->
                      <div v-if="showCustomerNotFoundMessage" class="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                        <div class="flex items-center gap-2">
                          <i class="pi pi-info-circle text-blue-600" style="font-size: 18px;"></i>
                          <p class="text-sm text-blue-800 font-medium">Customer not found. Please create a new customer.</p>
                        </div>
                      </div>
                      
                      <div class="grid grid-cols-2 gap-2">
                        <div class="mb-4">
                          <label class="block text-sm font-medium text-gray-700 mb-2">First Name *</label>
                          <input v-model="firstName" type="text" placeholder="First Name" required
                            class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:border-transparent" />
                        </div>
                        <div class="mb-4">
                          <label class="block text-sm font-medium text-gray-700 mb-2">Last Name</label>
                          <input v-model="lastName" type="text" placeholder="Last Name"
                            class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:border-transparent" />
                        </div>
                        <div class="mb-4">
                          <label class="block text-sm font-medium text-gray-700 mb-2">Email</label>
                          <input v-model="email" type="email" placeholder="Email"
                            class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:border-transparent"
                            :class="{ 'border-red-500': emailError }" />
                          <p v-if="emailError" class="text-xs text-red-600 mt-1">{{ emailError }}</p>
                        </div>
                        <div class="mb-4">
                          <label class="block text-sm font-medium text-gray-700 mb-2">Phone</label>
                          <input ref="phoneInputRef" type="tel" placeholder="(250) 555-0199"
                            class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:border-transparent"
                            :class="{ 'border-red-500': phoneError }" />
                          <p v-if="phoneError" class="text-xs text-red-600 mt-1">{{ phoneError }}</p>
                        </div>
                      </div>

                      <div class="flex gap-3 pt-4">
                        <button @click="handleContinue" :disabled="!canProceedToConfirm"
                          class="flex-1 bg-gray-700 text-white px-6 py-2 rounded-lg font-medium hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
                          Continue
                        </button>
                      </div>
                    </div>

                    <!-- Confirm Step: Order Summary -->
                    <div v-if="currentStep === 'confirm'" class="space-y-4">
                      <div class="mb-4 flex gap-12">
                        <div class="w-1/2">
                          <div class="flex items-center gap-2 mb-3">
                            <i class="pi pi-check-circle text-green-600" style="font-size: 20px;"></i>
                            <h3 class="text-lg font-semibold text-gray-800">Order Confirmation</h3>
                          </div>

                          <!-- Customer Info -->
                          <div class="bg-white border border-gray-200 rounded-lg p-4 mb-4">
                            <h4 class="text-sm font-medium text-gray-700 mb-2">Customer Information</h4>
                            <div class="space-y-1 text-sm text-gray-800">
                              <p><span class="font-medium">Name:</span> {{ firstName }} {{ lastName }}</p>
                              <p><span class="font-medium">Email:</span> {{ email }}</p>
                              <p v-if="phone"><span class="font-medium">Phone:</span> {{ phone }}</p>
                              <p v-if="customerData?.customerID"><span class="font-medium">Customer ID:</span> {{
                                customerData.customerID }}</p>
                            </div>
                          </div>

                          <!-- Billing Address -->
                          <div v-if="addressLine1 || city" class="bg-white border border-gray-200 rounded-lg p-4 mb-4">
                            <h4 class="text-sm font-medium text-gray-700 mb-2">Billing Address</h4>
                            <div class="text-sm text-gray-800">
                              <p>{{ addressLine1 }}</p>
                              <p v-if="addressLine2">{{ addressLine2 }}</p>
                              <p>{{ city }}{{ province ? `, ${province}` : '' }} {{ postalCode }}</p>
                              <p>{{ country }}</p>
                            </div>
                          </div>
                        </div>

                        <div class="w-1/2">
                          <!-- Pick Up In Store Toggle -->
                          <PickUpInStore v-model="pickUpInStore" v-model:selectedLocation="selectedPickupLocation"
                            :available-locations="availablePickupLocations" />

                          <!-- Shipping Address Toggle -->
                          <ShippingAddressToggle v-model="useShippingAddress" />

                          <!-- Shipping Address Form -->
                          <ShippingAddressForm v-if="useShippingAddress" v-model:addressLine1="addressLine1"
                            v-model:addressLine2="addressLine2" v-model:city="city" v-model:province="province"
                            v-model:postalCode="postalCode" v-model:country="country"
                            :address-line1-error="addressLine1Error" :postal-code-error="postalCodeError"
                            :available-provinces="availableProvinces" @validate-address-line1="validateAddressLine1"
                            @validate-postal-code="validatePostalCodeInput" @country-changed="postalCodeError = ''" />

                          <!-- Order Notes -->
                          <OrderNotes v-model="orderNotes" />

                          <div class="flex gap-3 pt-4">
                            <button @click="handleCreateOrder" :disabled="!canCreateOrder"
                              class="flex-1 bg-purple-600 text-white px-6 py-2 rounded-lg font-medium hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
                              Create Order
                            </button>
                          </div>
                        </div>

                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Transition>
      </div>
    </Transition>

    <!-- Order Summary Modal -->
    <Transition name="modal-outer">
      <div v-if="showOrderSummary" class="fixed inset-0 z-[100] flex items-center justify-center bg-black bg-opacity-50"
        @click.self="showOrderSummary = false">
        <Transition name="modal-inner">
          <div v-if="showOrderSummary" class="bg-white rounded-2xl shadow-xl p-8 max-w-2xl w-full mx-4">
            <div class="flex items-center justify-between mb-6">
              <h2 class="text-2xl font-semibold text-gray-800">Order Summary</h2>
              <i @click="showOrderSummary = false" class="pi pi-times cursor-pointer text-gray-500 hover:text-gray-700"
                style="font-size: 24px;"></i>
            </div>

            <div class="space-y-4 mb-6">
              <div class="flex justify-between py-2 border-b border-gray-200">
                <span class="text-gray-600 font-medium">Customer:</span>
                <span class="text-gray-800">{{ firstName }} {{ lastName }} • {{ email }} {{ phone ? `(${phone})` : ''
                  }}</span>
              </div>
              <div v-if="customerData?.customerID" class="flex justify-between py-2 border-b border-gray-200">
                <span class="text-gray-600 font-medium">Customer ID:</span>
                <span class="text-gray-800">{{ customerData.customerID }}</span>
              </div>
              <div v-if="addressLine1 || city" class="flex justify-between py-2 border-b border-gray-200">
                <span class="text-gray-600 font-medium">Billing Address:</span>
                <span class="text-gray-800">{{ addressLine1 }}{{ addressLine2 ? `, ${addressLine2}` : '' }}, {{ city
                  }}{{ province ? `, ${province}` : '' }} {{ postalCode }}, {{ country }}</span>
              </div>
              <div v-if="pickUpInStore" class="flex justify-between py-2 border-b border-gray-200">
                <span class="text-gray-600 font-medium">Pick Up In Store:</span>
                <span class="text-gray-800">{{ selectedPickupLocation || 'Not selected' }}</span>
              </div>
              <div v-if="useShippingAddress && (addressLine1 || city)"
                class="flex justify-between py-2 border-b border-gray-200">
                <span class="text-gray-600 font-medium">Shipping Address:</span>
                <span class="text-gray-800">{{ addressLine1 }}{{ addressLine2 ? `, ${addressLine2}` : '' }}, {{ city
                  }}{{ province ? `, ${province}` : '' }} {{ postalCode }}, {{ country }}</span>
              </div>
              <div v-if="orderNotes" class="flex justify-between py-2 border-b border-gray-200">
                <span class="text-gray-600 font-medium">Notes:</span>
                <span class="text-gray-800">{{ orderNotes }}</span>
              </div>
            </div>

            <div class="flex gap-4 justify-end">
              <button @click="showOrderSummary = false"
                class="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-300 transition-colors">
                Back
              </button>
              <button @click="handleConfirmOrder"
                class="px-6 py-2 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700 transition-colors">
                Confirm & Create Order
              </button>
            </div>
          </div>
        </Transition>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
.modal-outer-enter-active,
.modal-outer-leave-active {
  transition: opacity 0.3s cubic-bezier(0.52, 0.02, 0.19, 1.12);
}

.modal-outer-enter-from,
.modal-outer-leave-to {
  opacity: 0;
}

.modal-inner-enter-active {
  transition: all 0.3s cubic-bezier(0.52, 0.02, 0.19, 1.12) 0.15s;
}

.modal-inner-leave-active {
  transition: all 0.3s cubic-bezier(0.52, 0.02, 0.19, 1.12);
}

.modal-inner-enter-from {
  opacity: 0;
  transform: scale(0.8);
}

.modal-inner-leave-to {
  transform: scale(0.8);
}

input[type="number"]::-webkit-outer-spin-button,
input[type="number"]::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

input[type="number"] {
  -moz-appearance: textfield;
  appearance: textfield;
}

input.toggle:checked {
  background-color: #a855f7 ;
}
</style>